import {
  SHBox,
  SHButton,
  SHHtmlBlock,
  SHStack,
} from "@components/design-systems";
import { DownloadSVG, TooltipAllowSVG } from "@components/svgs";
import { DELAY_TIME } from "@constants";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisDTO } from "@models/platform-analysis/entities/analysis";
import { DisclaimerDTO } from "@models/platform-analysis/entities/disclaimer";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import {
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useTheme,
} from "@mui/material";
import { PDFPageContainer } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/pages";
import { PDFAnalysisOverviewSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/overview";
import {
  DEFAULT_GAP,
  PAGE_BODY_HEIGHT,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import {
  PageTemplate,
  PDFAnalysisControl,
  PDFAnalysisData,
} from "@pages/platform-analysis/components/buttons/export-pdf/model";
import {
  commentPagesHandler,
  disclaimerPageHandler,
  feesAnalysisGraphPageHandler,
  generatePDFData,
  objectivesPageHandler,
  platformsIncludedPageHandler,
} from "@pages/platform-analysis/components/buttons/export-pdf/util";
import { Document, pdf, View } from "@react-pdf/renderer";
import { hexToRGBA } from "@utils";
import { saveAs } from "file-saver";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { portfolioDetailPageHandler } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/fee-portfolio-details/utils";
import { feeAnalysisPortfolioCompositionPageHandler } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/portfolio-composition/utils";
import { portfolioFeeTablePageHandler } from "./components/sections/portfolio-fee-table/utils";
import { featureAnalysisPageHandler } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/feature-analysis/utils";
import { businessAnalysisPageHandler } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/business-analysis/utils";
import { feesAnalysisPageHandler } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/fee-analysis/utils";
import { featureIncludedPageHandler } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/features-include/utils";

interface ExportPDFButtonProps {
  isCompleted?: boolean;
  isPlatformAnalysis?: boolean;
}

export const ExportPDFButton = ({
  isCompleted,
  isPlatformAnalysis,
}: ExportPDFButtonProps): JSX.Element => {
  const { platformAnalysis, disclaimers, lineChartColors } =
    usePlatformAnalysis();
  const { palette, shadows } = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);

  const [isDisable, setIsDisable] = useState(false);

  useEffect(() => {
    setIsDisable(isDownloading || !isCompleted || !platformAnalysis);
  }, [isDownloading, isCompleted, platformAnalysis]);

  const handleOnDownloadPDF = async () => {
    if (!platformAnalysis) return <></>;
    setIsDownloading(true);
    const blob = await pdf(
      <PDFDocument
        platformAnalysis={platformAnalysis}
        disclaimers={disclaimers}
        lineChartColors={lineChartColors}
        isPlatformAnalysis={isPlatformAnalysis}
      />,
    ).toBlob();
    saveAs(
      blob,
      `SuitabilityHub Platform Analysis Summary - ${platformAnalysis?.summary?.name}.pdf`,
    );
    setIsDownloading(false);
  };

  const TooltipStyled = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "transparent",
      paddingRight: "0px",
    },
    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
      {
        marginTop: "-1px",
      },
  }));

  return (
    <TooltipStyled
      placement="top-end"
      enterDelay={DELAY_TIME}
      enterNextDelay={DELAY_TIME}
      title={
        isDisable && !isDownloading ? (
          <SHStack
            sx={{
              p: 1,
              mt: "3px",
              maxWidth: "190px",
              minHeight: "20px",
              bgcolor: hexToRGBA(palette.common.white, 0.85),
              border: "1px solid #E3E3E3",
              backdropFilter: "blur(2px)",
              borderRadius: "3px",
              boxShadow: shadows[1],
              position: "relative",
            }}
          >
            <SHHtmlBlock
              variant="body3"
              color={palette.text.disabled}
              content={"Complete your analysis to download it as PDF"}
              textAlign={"left"}
            />
            <SHBox sx={{ position: "absolute", top: "-8px", left: "82%" }}>
              <TooltipAllowSVG transform={"rotate(180)"} />
            </SHBox>
          </SHStack>
        ) : (
          ""
        )
      }
    >
      <SHBox component={"span"}>
        <SHButton
          variant="contained"
          size="extraMedium"
          startIcon={<DownloadSVG />}
          disabled={isDisable}
          isLoading={isDownloading}
          onClick={() => handleOnDownloadPDF()}
          sx={{
            "&.Mui-disabled.sh-btn-with-icon": {
              border: `1px solid ${hexToRGBA(palette.primary.dark, 0.3)}`,
              backgroundColor: palette.primary[50],
              color: palette.common.white,
            },
          }}
        >
          PDF
        </SHButton>
      </SHBox>
    </TooltipStyled>
  );
};

const PDFDocument = ({
  platformAnalysis,
  disclaimers,
  lineChartColors,
  isPlatformAnalysis,
}: {
  platformAnalysis: AnalysisDTO;
  disclaimers?: DisclaimerDTO[];
  lineChartColors?: { [key in string]: string };
  isPlatformAnalysis?: boolean;
}): JSX.Element => {
  const generatePDFPages = (pdfData: PDFAnalysisData) => {
    const pages: PageTemplate[] = [];
    const currentSteps: AnalysisStep[] = [];
    let spaceAvailable = PAGE_BODY_HEIGHT;
    const { Feature, BusinessMetric, Fee } = AnalysisStep;
    const {
      showFeeStep,
      showFeatureStep,
      showBusinessMetricStep,
      showSubSection,
      showComments,
      showSelectedFeatures,
      showHoldingNumber,
      showAnalysisScore,
      showFeeAnalysisGraph,
    } = pdfData?.controls as PDFAnalysisControl;

    if (showFeatureStep) currentSteps.push(Feature);
    if (showBusinessMetricStep) currentSteps.push(BusinessMetric);
    if (showFeeStep) currentSteps.push(Fee);

    const selectedProducts = pdfData?.selectedProducts ?? [];
    const isFamilyLevel =
      platformAnalysis?.summary?.portfolioSetup ===
      FeeCalculatorPortfolioLevel.Family;

    pages?.push({
      components: [
        <View key={pages?.length} style={{ gap: DEFAULT_GAP }}>
          <PDFAnalysisOverviewSection overview={pdfData?.overview} />
        </View>,
      ],
    });

    if (pdfData.objectives) {
      spaceAvailable = objectivesPageHandler(
        pages,
        pdfData.objectives,
        spaceAvailable,
        false,
        false,
      );
    }

    if (showFeatureStep) {
      spaceAvailable = featureAnalysisPageHandler({
        pages,
        spaceAvailable,
        featureAnalysis: pdfData?.featureAnalysis,
        showSubSection,
        isDetailMode:
          pdfData?.displayModes?.featureAnalysisTableViewMode ===
          TableViewMode.Detail,
        showAnalysisScore,
      });
    }

    if (showBusinessMetricStep) {
      spaceAvailable = businessAnalysisPageHandler({
        pages,
        spaceAvailable,
        businessAnalysis: pdfData?.businessAnalysis,
      });
    }

    if (showFeeStep) {
      const portfolioSetupLevel = pdfData?.portfolioSetup;

      spaceAvailable = portfolioDetailPageHandler({
        pages,
        spaceAvailable,
        feePortfolioDetails: pdfData?.feePortfolioDetails,
        isShowHoldingNumber: showHoldingNumber,
        portfolioSetup: portfolioSetupLevel,
        hiddenSubProductIds: pdfData?.hiddenSubProductIds,
        selectedSubProducts: pdfData?.feeAnalysis?.subProducts,
      });

      if (isFamilyLevel) {
        spaceAvailable = feeAnalysisPortfolioCompositionPageHandler({
          pages,
          spaceAvailable,
          portfolioCompositionData: pdfData?.portfolioComposition,
        });
      }

      if (!pdfData?.feeAnalysis) return;

      spaceAvailable = feesAnalysisPageHandler(
        pages,
        spaceAvailable,
        pdfData?.feeAnalysis,
        isFamilyLevel,
        pdfData.displayModes?.feesPerformanceScope,
      );

      if (showFeeAnalysisGraph && !isEmpty(pdfData.feeAnalysis.subProducts)) {
        spaceAvailable = feesAnalysisGraphPageHandler({
          pages,
          spaceAvailable,
          feeSubProducts: pdfData?.feeAnalysis?.subProducts,
          feesDisplayStyle: pdfData?.feeAnalysis?.feesDisplayStyle,
          fullFeeSubProducts:
            platformAnalysis?.summary?.feeAnalysis?.subProducts,
          colors: lineChartColors,
          isFamilyLevel,
          isPlatformAnalysis,
          tablePerformanceScope: pdfData.displayModes?.feesPerformanceScope,
          isExcludePerformanceFees:
            pdfData?.feeAnalysis?.isExcludePerformanceFees,
        });
      }

      if (platformAnalysis.summary?.smaPortfolioFee && isFamilyLevel) {
        spaceAvailable = portfolioFeeTablePageHandler({
          pages,
          spaceAvailable,
          smaPortfolioFee: platformAnalysis?.summary?.smaPortfolioFee,
          hiddenProductIndexMap:
            pdfData?.smaFeeTable?.hiddenProductsIndexMap ?? [],
          tableRows: pdfData?.smaFeeTable?.rows ?? [],
          tableViewMode: platformAnalysis?.summary?.feesDisplayStyle,
          tablePerformanceScope: pdfData.displayModes?.feesPerformanceScope,
        });
      }
    }

    if (showComments) {
      spaceAvailable = commentPagesHandler(
        pages,
        spaceAvailable,
        pdfData?.analysisComments ?? [],
        currentSteps,
      );
    }

    if (!isEmpty(pdfData?.concludingRemarks)) {
      if (pdfData?.concludingRemarks) {
        spaceAvailable = objectivesPageHandler(
          pages,
          pdfData?.concludingRemarks,
          spaceAvailable,
          true,
          false,
        );
      }
    }

    spaceAvailable = platformsIncludedPageHandler(
      pages,
      spaceAvailable,
      selectedProducts,
    );

    if (showSelectedFeatures && showFeatureStep) {
      spaceAvailable = featureIncludedPageHandler({
        pages,
        spaceAvailable,
        originalFeatureIncluded: pdfData?.featureIncluded,
      });
    }

    disclaimerPageHandler(
      pages,
      spaceAvailable,
      disclaimers ?? [],
      showFeeStep,
    );

    return pages;
  };

  const pdfData = generatePDFData(platformAnalysis);
  const pages = generatePDFPages(pdfData);

  return (
    <Document>
      {pages?.map((page, index) => (
        <PDFPageContainer
          key={index}
          pageIndex={index}
          totalPage={pages?.length}
          footerData={pdfData?.overview}
          isFirstPage={index === 0}
        >
          {page.components?.map((el, elIndex) => ({ ...el, key: elIndex }))}
        </PDFPageContainer>
      ))}
    </Document>
  );
};
